<template>
    <div class="e3-date-picker">
        <ButtonPopup
            ref="popup"
            class="w-100"
            :disabled="disabled"
            button-class="w-100 position-relative"
            popup-class="p-2"
            :popper-options="popperOptions"
            @close="onPopupClose">
            <input
                :id="id"
                :value="displayValue"
                :placeholder="label"
                class="form-control"
                :class="{'is-invalid': validation && validation.$error, 'date-range': showRange}"
                :readonly="inputReadonly"
                :disabled="disabled"
                @click.stop
                @blur="inputBlur"
                @focus="focus"
                @keydown="onInputKeydown"
                @input="inputChange"/>
            <span class="date-icon">
                <CalendarIcon size="18" class="text-primary"/>
            </span>
            <template #popup="{ close }">
                <CalendarTable
                    ref="calendar"
                    :value="dateVal"
                    :min-date="minDate"
                    :max-date="maxDate"
                    :disabled="disabled"
                    :date-disabled="dateDisabled"
                    @input="onCalendarInput($event, close)"/>
                <div v-if="reset" class="d-flex justify-content-center py-2">
                    <button
                        v-if="dateVal"
                        type="button"
                        class="btn btn-danger btn-sm"
                        @click="clearDate">
                        {{ $t("Clear date") }}
                    </button>
                </div>
            </template>
        </ButtonPopup>
        <ValitationMessages :validation="validation" :class="{'position-absolute': positionAbsoluteValidation}"/>
    </div>
</template>

<script>
import {mapGetters} from 'vuex';
import {academicYear} from '@services/helper.service';
import ButtonPopup from '@/common/components/button-popup/ButtonPopup.vue';
import CalendarTable from '@/common/components/datepicker/CalendarTable.vue';
import CalendarIcon from '@/common/components/svg-icons/CalendarIcon.vue';

export default {
    name: 'EDatepicker',
    props: {
        id: [String, Number],
        value: {
            type: String
        },
        minDate: String,
        maxDate: String,
        label: String,
        validation: Object,
        showRange: {
            type: String,
            default: ''
        },
        disabled: {
            type: Boolean,
            default: false
        },
        noWeekendsDays: {
            type: Boolean,
            default: false
        },
        reset: {
            type: Boolean,
            default: false
        },
        outputFormat: {
            type: String,
            default: 'YYYY-MM-DD'
        },
        displayFormat: {
            type: String,
            default: 'L'
        },
        displayMonthsFormat: {
            type: String,
            default: 'MMMM YYYY'
        },
        displayYearsFormat: {
            type: String,
            default: 'YYYY'
        },
        disableType: {
            type: Boolean,
            default: false
        },
        displayYearPeriod: {
            type: Boolean,
            default: false
        },
        yearDuration: String,
        positionAbsoluteValidation: {
            type: Boolean,
            default: false
        },
        popperOptions: {
            type: Object,
            default: () => ({
                placement: 'bottom-end',
                modifiers: [
                    { name: 'arrow', enabled: false },
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 2]
                        }
                    },
                    {
                        name: 'flip',
                        options: {
                            fallbackPlacements: ['bottom-start', 'top-end', 'top-start', 'right', 'left']
                        }
                    }
                ]
            })
        },
        disabledDates: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            inputFocused: false,
            typedValue: null
        };
    },
    computed: {
        ...mapGetters(['language']),
        dateVal: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        },
        inputReadonly() {
            return this.showRange || this.disableType;
        },
        displayValue() {
            const language = this.language;
            if (this.typedValue !== null) {
                return this.typedValue;
            }
            return this.setDisplayDate(this.value, this.outputFormat, this.displayFormat, language);
        }
    },
    methods: {
        focus() {
            if (!this.disabled) {
                this.inputFocused = true;
                this.openCalendar();
            }
        },
        openCalendar() {
            const popup = this.$refs.popup;
            if (popup) {
                popup.openPopup();
            }
        },
        inputBlur() {
            this.inputFocused = false;
            this.typedValue = null;
        },
        onInputKeydown(event) {
            if (event.key !== 'ArrowDown') {
                return;
            }
            const popup = this.$refs.popup;
            if (!popup || !popup.popupVisible) {
                return;
            }
            event.preventDefault();
            this.$nextTick(() => {
                if (this.$refs.calendar) {
                    this.$refs.calendar.focusFocusedDay();
                }
            });
        },
        onPopupClose() {
            if (this.$refs.calendar) {
                this.$refs.calendar.resetView();
            }
            this.typedValue = null;
            if (this.dateVal && this.inputFocused) {
                const nextDate = this.checkMinMaxDate(this.dateVal, this.outputFormat);
                if (nextDate !== null) {
                    this.dateVal = nextDate;
                }
            }
        },
        clearDate() {
            this.typedValue = null;
            this.$emit('input', '');
        },
        dateDisabled(ymd, date) {
            const isWeekend = this.$moment(date).day() === 6 || this.$moment(date).day() === 0;
            const isListed = this.disabledDates.length && this.disabledDates.includes(this.$moment(date).format('YYYY-MM-DD'));
            return !!((this.noWeekendsDays && isWeekend) || isListed);
        },
        setDisplayDate(date, inputFormat = this.outputFormat, outputFormat = this.displayFormat, language = this.language) {
            if (date && this.showRange) {
                switch (this.showRange) {
                case 'years':
                    if (this.displayYearPeriod) {
                        return this.$moment(academicYear(date), inputFormat).locale(language).startOf('isoYear').format(this.displayYearsFormat) +
                            ' - ' + this.$moment(this.yearDuration, inputFormat).locale(language).format(this.displayYearsFormat);
                    } else {
                        return this.$moment(date, inputFormat).locale(language).startOf('isoYear').format(this.displayYearsFormat);
                    }
                case 'months':
                    return this.$moment(date, inputFormat).locale(language).startOf('isoMonth').format(this.displayMonthsFormat);
                default:
                    return this.$moment(date, inputFormat).locale(language).startOf('isoWeek').format(outputFormat) +
                        ' - ' +
                        this.$moment(date, inputFormat).locale(language).endOf('isoWeek').format(outputFormat);
                }
            } else if (date) {
                return this.$moment(date, inputFormat).locale(language).format(outputFormat);
            }
            return '';
        },
        onCalendarInput(e, close) {
            this.pickerInput(e);
            if (typeof close === 'function') {
                close();
            }
        },
        pickerInput(e) {
            this.typedValue = null;
            this.dateVal = e;
        },
        checkMinMaxDate(date, inputFormat = this.displayFormat, outputFormat = this.outputFormat) {
            const parsed = this.$moment(date, inputFormat);
            if (!parsed.isValid()) {
                return null;
            }

            if (this.minDate && parsed.isBefore(this.$moment(this.minDate))) {
                return this.$moment(this.minDate).format(outputFormat);
            }

            if (this.maxDate && parsed.isAfter(this.$moment(this.maxDate))) {
                return this.$moment(this.maxDate).format(outputFormat);
            }

            return parsed.format(outputFormat);
        },
        inputChange(e) {
            this.typedValue = e.target.value;
            if (e.target.value === '') {
                this.dateVal = '';
                return;
            }
            const nextDate = this.checkMinMaxDate(e.target.value);
            if (nextDate !== null) {
                this.dateVal = nextDate;
            }
        }
    },
    components: {
        ButtonPopup,
        CalendarTable,
        CalendarIcon
    }
};
</script>

<style scoped lang="scss">
@import "@common/styles/variables/_variables.scss";

.date-range {
    min-width: 220px;
}

@media screen and (max-width: $grid-br-sm) {
    .date-range {
        min-width: 198px;
    }
}

.e3-date-picker .form-control {
    padding: 0.375rem 0.55rem;
}

.date-icon {
    position: absolute;
    top: 4px;
    right: 0;
    pointer-events: none;
    padding: 0.375rem 0.55rem;
    line-height: 1;
}

.form-control.is-invalid {
    background-image: none;
    border: 1px solid $danger !important;
}
</style>
